// My Notifications — per-member channel setup: browser Web Push, SMS, email,
// and per-event toggles. Console-standard redesign (June 2026): ConsoleShell +
// theme tokens so it matches the rest of the admin in light and dark.
import React, { useEffect, useState } from 'react';
import { ConsoleShell, Panel, Empty, useConsoleTheme, F } from '../components/Console';
import { pushSupported, currentPermission, isSubscribed, enablePush, disablePush, sendTestPush } from '../utils/webpush';

function Toggle({ on, onClick, t }) {
  return (
    <button onClick={onClick} style={{ width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', background: on ? t.accent : t.gbar, position: 'relative', flexShrink: 0, transition: 'background .15s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }} />
    </button>
  );
}

function Row({ title, desc, children, t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderTop: `1px solid ${t.bd}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: t.tx }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: t.mut, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
      </div>
      {children}
    </div>
  );
}

function NotifSettingsBody({ session }) {
  const { mode, t } = useConsoleTheme();
  const email = (session && session.user && session.user.email) || '';
  const [prefs, setPrefs] = useState(null);
  const [pushState, setPushState] = useState('unknown'); // unknown|on|off|denied|unsupported
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!email) return;
    fetch('/api/team-notif-prefs?email=' + encodeURIComponent(email)).then(r => r.json()).then(d => setPrefs(d.prefs || {})).catch(() => setPrefs({}));
    (async () => {
      if (!pushSupported()) { setPushState('unsupported'); return; }
      const perm = await currentPermission();
      if (perm === 'denied') { setPushState('denied'); return; }
      setPushState((await isSubscribed()) ? 'on' : 'off');
    })();
  }, [email]);

  function save(patch) {
    const next = { ...prefs, ...patch }; setPrefs(next);
    fetch('/api/team-notif-prefs', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, ...patch }) }).catch(() => {});
  }
  function flash(m) { setMsg(m); setTimeout(() => setMsg(''), 2600); }

  async function togglePush() {
    setBusy(true);
    try {
      if (pushState === 'on') { await disablePush(); setPushState('off'); save({ web_push: false }); flash('Browser notifications off'); }
      else { await enablePush(email); setPushState('on'); save({ web_push: true }); flash('Browser notifications on'); }
    } catch (e) { flash(e.message || 'Could not enable'); }
    setBusy(false);
  }

  if (!email) return <Empty>Sign in to manage notifications.</Empty>;
  if (!prefs) return <Empty>Loading…</Empty>;

  const ctaText = mode === 'dark' ? '#06121c' : '#E0F5FC';

  return (
    <div style={{ maxWidth: 760, fontFamily: F }}>
      <div style={{ fontSize: '0.9rem', color: t.mut, margin: '2px 0 16px', lineHeight: 1.5 }}>
        Get pinged about calls, mentions, tickets and scheduled meetings — even when you're not in the dashboard.
        Notifications deep-link straight back here.
      </div>

      <Panel title='Channels' style={{ marginBottom: 14 }}>
        <Row t={t} title="Browser notifications" desc={pushState === 'unsupported' ? 'Not supported on this browser' : pushState === 'denied' ? 'Blocked — enable in browser site settings' : 'Desktop & Android alerts even when this tab is closed'}>
          {pushState === 'unsupported' || pushState === 'denied'
            ? <span style={{ fontSize: '0.8rem', color: t.danger, fontWeight: 600 }}>{pushState === 'denied' ? 'Blocked' : 'N/A'}</span>
            : <button onClick={togglePush} disabled={busy} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: F, fontSize: '0.8rem', fontWeight: 700, background: pushState === 'on' ? t.live : t.accent, color: pushState === 'on' ? t.livec : ctaText }}>{busy ? '…' : pushState === 'on' ? '✓ Enabled' : 'Enable'}</button>}
        </Row>
        <Row t={t} title="Email" desc={'Sent to ' + email}>
          <Toggle t={t} on={prefs.email_notify !== false} onClick={() => save({ email_notify: !(prefs.email_notify !== false) })} />
        </Row>
        <Row t={t} title="SMS text message" desc="Urgent fallback to your phone (needs Twilio configured by an admin)">
          <Toggle t={t} on={!!prefs.sms} onClick={() => save({ sms: !prefs.sms })} />
        </Row>
        {prefs.sms && (
          <div style={{ paddingTop: 4 }}>
            <input value={prefs.sms_number || ''} onChange={e => setPrefs({ ...prefs, sms_number: e.target.value })} onBlur={e => save({ sms_number: e.target.value })} placeholder="+1 555 123 4567"
              style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${t.bd}`, borderRadius: 9, padding: '10px 12px', fontSize: '0.85rem', fontFamily: F, outline: 'none', color: t.tx, background: t.period }} />
          </div>
        )}
      </Panel>

      <Panel title='Notify me about' style={{ marginBottom: 14 }}>
        <Row t={t} title="Incoming calls" desc="Someone starts or invites you to a Team Room call"><Toggle t={t} on={prefs.notify_calls !== false} onClick={() => save({ notify_calls: !(prefs.notify_calls !== false) })} /></Row>
        <Row t={t} title="Mentions" desc="You're @mentioned in a channel or DM"><Toggle t={t} on={prefs.notify_mentions !== false} onClick={() => save({ notify_mentions: !(prefs.notify_mentions !== false) })} /></Row>
        <Row t={t} title="Support tickets" desc="A ticket is assigned or escalated to you"><Toggle t={t} on={prefs.notify_tickets !== false} onClick={() => save({ notify_tickets: !(prefs.notify_tickets !== false) })} /></Row>
        <Row t={t} title="Scheduled meetings" desc="A call is scheduled or about to start"><Toggle t={t} on={prefs.notify_schedule !== false} onClick={() => save({ notify_schedule: !(prefs.notify_schedule !== false) })} /></Row>
      </Panel>

      {pushState === 'on' && (
        <button onClick={() => { sendTestPush(email); flash('Test sent'); }}
          style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${t.bd}`, background: t.period, color: t.tx, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: F }}>
          Send me a test notification
        </button>
      )}
      {msg && <div style={{ marginTop: 14, fontSize: '0.82rem', color: t.up, fontWeight: 600 }}>{msg}</div>}
    </div>
  );
}

export default function NotifSettings(props) {
  if (props && props.embed) return <NotifSettingsBody {...props} />;
  return <ConsoleShell title="My Notifications"><NotifSettingsBody {...props} /></ConsoleShell>;
}
